import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyMentions, filterParticipants, findMentionQuery, mentionLabelOf } from "./mentionCompose";

// Menções já escolhidas por conversa: trocar de conversa e voltar (com o rascunho) não as perde.
const pickedByTicket = new Map();

// Estado das sugestões de "@" do campo de mensagem de um grupo.
const useMentionCompose = ({ enabled, ticketId, participants, value, setValue, inputRef }) => {
  const [query, setQuery] = useState(null); // { start, query }
  const [activeIndex, setActiveIndex] = useState(0);
  const pickedRef = useRef([]);

  useEffect(() => {
    pickedRef.current = pickedByTicket.get(ticketId) || [];
    setQuery(null);
    return () => {
      if (pickedRef.current.length > 0) pickedByTicket.set(ticketId, pickedRef.current);
      else pickedByTicket.delete(ticketId);
    };
  }, [ticketId]);

  const suggestions = useMemo(
    () => (enabled && query ? filterParticipants(participants, query.query) : []),
    [enabled, query, participants]
  );
  const open = suggestions.length > 0;

  useEffect(() => {
    setActiveIndex(0);
  }, [query && query.query, suggestions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // chamado a cada mudança de texto ou de posição do cursor
  const track = useCallback(
    (element) => {
      if (!enabled || !element) {
        setQuery(null);
        return;
      }
      const caret = element.selectionStart;
      if (caret === null || caret === undefined || caret !== element.selectionEnd) {
        setQuery(null);
        return;
      }
      setQuery(findMentionQuery(element.value, caret));
    },
    [enabled]
  );

  const close = useCallback(() => setQuery(null), []);

  const choose = useCallback(
    (participant) => {
      if (!participant || !query) return;
      const element = inputRef.current;
      const text = String(value == null ? "" : value);
      const caret = element && element.selectionStart != null ? element.selectionStart : query.start + query.query.length + 1;
      let label = mentionLabelOf(participant);
      // dois participantes com o mesmo nome: o segundo leva o fim do número para não confundir
      const clash = pickedRef.current.find((item) => item.label === label && item.participant.id !== participant.id);
      if (clash) label = `${label} (${(participant.number || participant.user).slice(-4)})`;
      const next = `${text.slice(0, query.start)}${label} ${text.slice(caret)}`;
      pickedRef.current = [
        ...pickedRef.current.filter((item) => item.participant.id !== participant.id || item.label !== label),
        { label, participant },
      ];
      setValue(next);
      setQuery(null);
      const position = query.start + label.length + 1;
      setTimeout(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        try {
          el.setSelectionRange(position, position);
        } catch (e) {
          /* campo sem suporte a seleção */
        }
      }, 0);
    },
    [query, value, setValue, inputRef]
  );

  // true = a tecla foi consumida pelas sugestões (não envia, não navega nas respostas rápidas)
  const onKeyDown = useCallback(
    (e) => {
      if (!open) return false;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return true;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        choose(suggestions[Math.min(activeIndex, suggestions.length - 1)]);
        return true;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setQuery(null);
        return true;
      }
      return false;
    },
    [open, suggestions, activeIndex, choose]
  );

  // texto pronto para envio: { body, mentions } (mentions vazio = nada a declarar)
  const resolve = useCallback(
    (text) => (enabled ? applyMentions(text, pickedRef.current) : { body: text, mentions: [] }),
    [enabled]
  );

  const reset = useCallback(() => {
    pickedRef.current = [];
    pickedByTicket.delete(ticketId);
    setQuery(null);
  }, [ticketId]);

  return { open, suggestions, activeIndex, setActiveIndex, track, choose, close, onKeyDown, resolve, reset };
};

export default useMentionCompose;
